import React, { useEffect, useRef, useState } from "react";
import { Form, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    UploadOutlined,
    CheckOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { uploadDealApprovalFile, approveJVDeal, rejectJVDeal } from "../../../redux/slices/DealApprovalSlice";
import { DEAL_APPROVAL_SUCCESS_MESSAGES, getDealApprovalSuccessMessage } from "../../../constants/constants";
import RejectDealModal from "../../../components/RejectDealModal/RejectDealModal";
import ReferenceLinksFormList from "../../../components/ReferenceLinksFormList/ReferenceLinksFormList";
import {
    formatReferenceLinks,
    parseReferenceLinks,
} from "../../../helpers/referenceLinks";

const ProceedDetailsView = ({ dealId, dealData = null }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const hydratedDealRef = useRef(null);
    const hydratedRefLinksRef = useRef(null);
    const [refLinksForm] = Form.useForm();
    const [comments, setComments] = useState("");
    const [approvedFundAmt, setApprovedFundAmt] = useState("");
    const [minInvestmentCriteria, setMinInvestmentCriteria] = useState("");
    const [expectedRoi, setExpectedRoi] = useState("");
    const [profitShare, setProfitShare] = useState("");
    const [approvedFundAmtError, setApprovedFundAmtError] = useState("");
    const [minInvestmentCriteriaError, setMinInvestmentCriteriaError] = useState("");
    const [expectedRoiError, setExpectedRoiError] = useState("");
    const [profitShareError, setProfitShareError] = useState("");
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const {
        uploadFileLoading,
        uploadedFiles = [],
        allDealsList = [],
        approveJVDealLoading,
        rejectJVDealLoading,
    } = useSelector((state) => state.dealApproval);
    const matchedDeal = Array.isArray(allDealsList)
        ? allDealsList.find((deal) => String(deal?.dealId) === String(dealId))
        : null;
    const effectiveDeal = matchedDeal || dealData || null;

    const parseAmount = (value) => {
        if (value === null || value === undefined) return NaN;
        if (typeof value === "number") return value;
        const normalized = String(value).replace(/[^0-9.-]/g, "");
        return Number(normalized);
    };

    const formatAmountIndian = (value) => {
        const parsed = parseAmount(value);
        if (!Number.isFinite(parsed)) return "N/A";
        return parsed.toLocaleString("en-IN");
    };

    const normalizeAmountInput = (value) => String(value || "").replace(/[^0-9]/g, "");

    const formatAmountInput = (value) => {
        const normalized = normalizeAmountInput(value);
        if (!normalized) return "";
        return Number(normalized).toLocaleString("en-IN");
    };

    useEffect(() => {
        if (!dealId) {
            hydratedDealRef.current = null;
            return;
        }

        if ((!Array.isArray(allDealsList) || allDealsList.length === 0) && !dealData) return;
        if (!effectiveDeal || hydratedDealRef.current === String(dealId)) return;

        if (effectiveDeal?.expectedRoi != null && effectiveDeal.expectedRoi !== "") {
            setExpectedRoi(String(effectiveDeal.expectedRoi));
        }

        const parsedProfitShare = Number(effectiveDeal?.profitShare);
        if (Number.isFinite(parsedProfitShare)) {
            setProfitShare(String(parsedProfitShare));
        }

        hydratedDealRef.current = String(dealId);
    }, [effectiveDeal, allDealsList, dealId, dealData]);

    useEffect(() => {
        if (!dealId) {
            hydratedRefLinksRef.current = null;
            return;
        }

        if ((!Array.isArray(allDealsList) || allDealsList.length === 0) && !dealData) return;
        if (!effectiveDeal || hydratedRefLinksRef.current === String(dealId)) return;

        refLinksForm.setFieldsValue({
            otherLinks: parseReferenceLinks(effectiveDeal?.referenceLinks),
        });
        hydratedRefLinksRef.current = String(dealId);
    }, [effectiveDeal, allDealsList, dealId, dealData, refLinksForm]);

    const handleChooseFilesClick = () => {
        fileInputRef.current?.click();
    };

    const handleApproveDeal = async () => {
        setApprovedFundAmtError("");
        setMinInvestmentCriteriaError("");
        setExpectedRoiError("");
        setProfitShareError("");

        const isApprovedFundAmtMissing = !approvedFundAmt;
        const isMinInvestmentCriteriaMissing = !minInvestmentCriteria;
        const isExpectedRoiMissing = expectedRoi === "" || expectedRoi === null || expectedRoi === undefined;
        const isProfitShareMissing = profitShare === "" || profitShare === null || profitShare === undefined;

        if (isApprovedFundAmtMissing || isMinInvestmentCriteriaMissing || isExpectedRoiMissing || isProfitShareMissing) {
            if (isApprovedFundAmtMissing) {
                setApprovedFundAmtError("Please enter approved fund amount");
                message.error("Please enter approved fund amount");
            }

            if (isMinInvestmentCriteriaMissing) {
                setMinInvestmentCriteriaError("Please enter minimum investment criteria");
                message.error("Please enter minimum investment criteria");
            }

            if (isExpectedRoiMissing) {
                setExpectedRoiError("Please enter ROI");
                message.error("Please enter ROI");
            }

            if (isProfitShareMissing) {
                setProfitShareError("Please enter profit share");
                message.error("Please enter profit share");
            }

            return;
        }

        const parsedApprovedFundAmt = Number(approvedFundAmt);
        const parsedMinInvestmentCriteria = Number(minInvestmentCriteria);
        const parsedExpectedRoi = Number(expectedRoi);
        const parsedProfitShare = Number(profitShare);

        if (parsedApprovedFundAmt < 200000) {
            setApprovedFundAmtError("Approved fund amount must be at least 200000");
            return;
        }

        const parsedFundRequired = parseAmount(effectiveDeal?.fundRequired);
        if (!Number.isNaN(parsedFundRequired) && parsedFundRequired > 0 && parsedApprovedFundAmt > parsedFundRequired) {
            setApprovedFundAmtError("Approved fund amount cannot be greater than fund required");
            return;
        }

        if (parsedMinInvestmentCriteria < 200000) {
            setMinInvestmentCriteriaError("Minimum investment criteria must be at least 200000");
            return;
        }

        if (parsedMinInvestmentCriteria > parsedApprovedFundAmt) {
            setMinInvestmentCriteriaError("Minimum investment criteria cannot exceed approved fund amount");
            return;
        }

        if (!Number.isFinite(parsedExpectedRoi) || parsedExpectedRoi <= 0) {
            setExpectedRoiError("ROI must be greater than 0");
            return;
        }

        if (parsedExpectedRoi >= 100) {
            setExpectedRoiError("ROI should be less than 100(%)");
            return;
        }

        if (!Number.isFinite(parsedProfitShare) || parsedProfitShare < 0) {
            setProfitShareError("Profit share cannot be negative");
            return;
        }

        if (parsedProfitShare > 100) {
            setProfitShareError("Profit share should not exceed 100(%)");
            return;
        }

        if (uploadedFiles.length === 0) {
            message.error("Please upload the documents");
            return;
        }

        const trimmedComments = comments.trim();

        if (trimmedComments && trimmedComments.length < 10) {
            message.error("Comments should be at least 10 characters");
            return;
        }

        let referenceLinks;
        try {
            const refValues = await refLinksForm.validateFields();
            referenceLinks = formatReferenceLinks(refValues.otherLinks);
        } catch {
            message.error("Please fix reference link validation errors");
            return;
        }

        const finalizedDocuments = uploadedFiles.map((file) => ({
            fileName: file.fileName,
            fileUrl: file.fileUrl,
            fileType: file.fileType || "",
        }));
        const pitchDeck = finalizedDocuments[finalizedDocuments.length - 1]?.fileUrl || null;
        const payload = {
            dealId,
            finalizedDocuments,
            ...(pitchDeck && { pitchDeck }),
            comments: trimmedComments,
            approvedFundAmt: parsedApprovedFundAmt,
            minInvestmentCriteria: parsedMinInvestmentCriteria,
            expectedRoi: parsedExpectedRoi,
            profitShare: parsedProfitShare,
            referenceLinks,
        };

        const result = await dispatch(approveJVDeal(payload));
        if (result?.meta?.requestStatus !== "fulfilled") {
            message.error(result?.payload || "Failed to approve deal. Please try again.");
            return;
        }

        message.success(
            getDealApprovalSuccessMessage(
                DEAL_APPROVAL_SUCCESS_MESSAGES.DEAL_APPROVED,
                dealId,
            ),
        );
        navigate("/deals-management", { state: { activeTab: "joint-ventures-deals" } });
    };

    const handleRejectDeal = async () => {
        if (!dealId) {
            message.error("Deal ID is missing");
            return;
        }

        const trimmedReason = rejectReason.trim();
        if (!trimmedReason) {
            message.error("Please enter reason for reject");
            return;
        }

        const result = await dispatch(rejectJVDeal({ dealId, comments: trimmedReason }));
        if (result?.meta?.requestStatus !== "fulfilled") {
            const errorMessage =
                result?.payload?.message ||
                result?.payload?.error ||
                result?.payload ||
                "Failed to reject deal. Please try again.";
            message.error(`Deal ${dealId}: ${errorMessage}`);
            return;
        }

        message.success(
            getDealApprovalSuccessMessage(
                DEAL_APPROVAL_SUCCESS_MESSAGES.DEAL_REJECTED,
                dealId,
            ),
        );
        setRejectReason("");
        setIsRejectModalOpen(false);
        navigate("/deals-management", { state: { activeTab: "joint-ventures-deals" } });
    };

    const handleFileSelection = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (!selectedFiles.length) return;

        const folderPath = `deals/${dealId || "common"}`;
        const latestFile = selectedFiles[selectedFiles.length - 1];

        const allowedExtensions = [".pdf", ".doc", ".docx"];
        const lowerFileName = String(latestFile?.name || "").toLowerCase();
        const isAllowedFile = allowedExtensions.some((extension) =>
            lowerFileName.endsWith(extension)
        );

        if (!isAllowedFile) {
            message.error("Only .pdf, .doc, and .docx files are allowed");
            event.target.value = "";
            return;
        }

        // Replace spaces with underscores
        const sanitizedFileName = latestFile.name.replace(/\s+/g, "_");

        const sanitizedFile = new File(
            [latestFile],
            sanitizedFileName,
            {
                type: latestFile.type,
                lastModified: latestFile.lastModified,
            }
        );

        const result = await dispatch(
            uploadDealApprovalFile({
                file: sanitizedFile,
                folderPath,
            })
        );

        if (result?.meta?.requestStatus !== "fulfilled") {
            message.error(`Failed to upload ${sanitizedFileName}`);
        } else {
            message.success(`${sanitizedFileName} uploaded successfully`);
        }

        event.target.value = "";
    };

    return (
        <div className="deal-main-column">
            <section className="panel-card">
                <div className="panel-title-row">
                    <h3>Joint Venture Details</h3>
                    {/* <span className="deal-chip">Joint Ventures</span> */}
                </div>

                <div className="details-grid">
                    <div>
                        <label>Project Name</label>
                        <p>{effectiveDeal?.dealName || "N/A"}</p>
                    </div>
                    <div>
                        <label>Project Duration</label>
                        <p>{effectiveDeal?.dealDuration || "N/A"} </p>
                    </div>
                    <div>
                        <label>Project Value</label>
                        <p>{formatAmountIndian(effectiveDeal?.dealValue)}</p>
                    </div>
                    <div>
                        <label>Fund Required</label>
                        <p>{formatAmountIndian(effectiveDeal?.fundRequired)}</p>
                    </div>
                    <div>
                        <label>Platform Fee</label>
                        {/* <p>{effectiveDeal?.roi || "N/A"}</p> */}
                        <p>3%</p>
                    </div>
                </div>

                {/* <div className="divider" /> */}

                {/* <div className="criteria-block">
                    <label>Minimum Criteria</label>
                    <p>{effectiveDeal?.minDealAmt || "N/A"}</p>
                </div> */}
            </section>

            <section className="panel-card">
                <h3>Approved Fund Details <span style={{ color: '#ef4444' }}>*</span></h3>
                <p className="panel-subtext">Enter the approved fund amount, minimum investment criteria, ROI and profit share for this deal</p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '12px' }}>Approved Fund Amount <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="e.g., 20,00,000"
                            value={formatAmountInput(approvedFundAmt)}
                            onChange={(e) => {
                                const nextValue = normalizeAmountInput(e.target.value);
                                setApprovedFundAmt(nextValue);

                                if (nextValue && Number(nextValue) < 200000) {
                                    setApprovedFundAmtError("Approved fund amount must be at least 2,00,000");
                                } else {
                                    setApprovedFundAmtError("");
                                }

                                const parsedFundRequired = parseAmount(effectiveDeal?.fundRequired);
                                const parsedNextValue = parseAmount(nextValue);
                                if (nextValue && !Number.isNaN(parsedFundRequired) && parsedFundRequired > 0 && parsedNextValue > parsedFundRequired) {
                                    setApprovedFundAmtError("Approved fund amount cannot be greater than fund required");
                                } else if (!nextValue || Number(nextValue) >= 200000) {
                                    setApprovedFundAmtError("");
                                }

                                if (!nextValue || !minInvestmentCriteria) {
                                    setMinInvestmentCriteriaError("");
                                    return;
                                }

                                if (Number(minInvestmentCriteria) > Number(nextValue)) {
                                    setMinInvestmentCriteriaError("Minimum investment criteria cannot exceed approved fund amount");
                                    return;
                                }

                                setMinInvestmentCriteriaError("");
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: approvedFundAmtError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                                borderRadius: '4px',
                                fontSize: '14px',
                            }}
                        />
                        {approvedFundAmtError ? (
                            <p style={{ color: '#ff4d4f', marginTop: '6px', marginBottom: 0, fontSize: '12px' }}>
                                {approvedFundAmtError}
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '12px' }}>Minimum Investment Criteria <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="e.g., 2,00,000"
                            value={formatAmountInput(minInvestmentCriteria)}
                            onChange={(e) => {
                                const nextValue = normalizeAmountInput(e.target.value);
                                setMinInvestmentCriteria(nextValue);

                                if (nextValue && Number(nextValue) < 200000) {
                                    setMinInvestmentCriteriaError("Minimum investment criteria must be at least 2,00,000");
                                    return;
                                }

                                if (!nextValue || !approvedFundAmt) {
                                    setMinInvestmentCriteriaError("");
                                    return;
                                }

                                if (Number(nextValue) > Number(approvedFundAmt)) {
                                    setMinInvestmentCriteriaError("Minimum investment criteria cannot exceed approved fund amount");
                                    return;
                                }

                                setMinInvestmentCriteriaError("");
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: minInvestmentCriteriaError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                                borderRadius: '4px',
                                fontSize: '14px',
                            }}
                        />
                        {minInvestmentCriteriaError ? (
                            <p style={{ color: '#ff4d4f', marginTop: '6px', marginBottom: 0, fontSize: '12px' }}>
                                {minInvestmentCriteriaError}
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '12px' }}>Expected ROI (%) <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="e.g., 18.5"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                            value={expectedRoi}
                            onChange={(e) => {
                                const nextValue = e.target.value;
                                setExpectedRoi(nextValue);

                                if (!nextValue) {
                                    setExpectedRoiError("");
                                    return;
                                }

                                const parsedValue = Number(nextValue);
                                if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
                                    setExpectedRoiError("ROI must be greater than 0");
                                    return;
                                }

                                if (parsedValue >= 100) {
                                    setExpectedRoiError("ROI should be less than 100(%)");
                                    return;
                                }

                                setExpectedRoiError("");
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: expectedRoiError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                                borderRadius: '4px',
                                fontSize: '14px',
                            }}
                        />
                        {expectedRoiError ? (
                            <p style={{ color: '#ff4d4f', marginTop: '6px', marginBottom: 0, fontSize: '12px' }}>
                                {expectedRoiError}
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '12px' }}>Profit Share (%) <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="e.g., 30"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                            value={profitShare}
                            onChange={(e) => {
                                const nextValue = e.target.value;
                                setProfitShare(nextValue);

                                if (!nextValue) {
                                    setProfitShareError("");
                                    return;
                                }

                                const parsedValue = Number(nextValue);
                                if (!Number.isFinite(parsedValue) || parsedValue < 0) {
                                    setProfitShareError("Profit share cannot be negative");
                                    return;
                                }

                                if (parsedValue > 100) {
                                    setProfitShareError("Profit share should not exceed 100(%)");
                                    return;
                                }

                                setProfitShareError("");
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: profitShareError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                                borderRadius: '4px',
                                fontSize: '14px',
                            }}
                        />
                        {profitShareError ? (
                            <p style={{ color: '#ff4d4f', marginTop: '6px', marginBottom: 0, fontSize: '12px' }}>
                                {profitShareError}
                            </p>
                        ) : null}
                    </div>
                </div>
            </section>

            <section className="panel-card">
                <h3>Additional Reference Links</h3>
                <p className="panel-subtext">Review and update reference links before approving this deal</p>

                <Form form={refLinksForm} layout="vertical">
                    <ReferenceLinksFormList />
                </Form>
            </section>

            <section className="panel-card">
                <h3>Upload Finalized Documents <span style={{ color: '#ef4444' }}>*</span></h3>
                <p className="panel-subtext">Upload the finalized agreement and supporting documents for approval</p>

                <div className="upload-dropzone">
                    <UploadOutlined onClick={handleChooseFilesClick} />
                    <h4>Click to upload finalized documents</h4>
                    <p>PDF and Word files accepted</p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        style={{ display: "none" }}
                        onChange={handleFileSelection}
                    />
                    <button
                        type="button"
                        className="file-btn"
                        disabled={uploadFileLoading}
                        onClick={handleChooseFilesClick}
                    >
                        {uploadFileLoading ? "Uploading..." : "Choose Files"}
                    </button>
                    {uploadedFiles.length > 0 && (
                        <div className="uploaded-file-list">
                            {uploadedFiles.map((file, index) => (
                                <p key={`${file.fileUrl || file.fileName}-${index}`}>
                                    {file.fileName || "Uploaded file"}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="panel-card">
                <h3>Comments &amp; Notes</h3>
                <textarea
                    rows={4}
                    maxLength={500}
                    placeholder="Add your comments, notes, or reasons for approval/rejection..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                    <p className="panel-helper" style={{ margin: 0 }}>These comments will be visible in the deal history</p>
                    <span style={{ fontSize: '12px', color: comments.length >= 500 ? '#9ca3af' : '#6b7280' }}>
                        {comments.length}/500
                    </span>
                </div>
            </section>

            <section className="panel-card action-card">
                <button type="button" className="btn btn-approve" disabled={approveJVDealLoading} onClick={handleApproveDeal}>
                    <CheckOutlined /> {approveJVDealLoading ? "Approving..." : "Approve Deal"}
                </button>
                <button
                    type="button"
                    className="btn btn-reject"
                    disabled={rejectJVDealLoading}
                    onClick={() => {
                        setRejectReason("");
                        setIsRejectModalOpen(true);
                    }}
                >
                    <CloseOutlined /> {rejectJVDealLoading ? "Rejecting..." : "Reject Deal"}
                </button>
                <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => navigate("/deals-management", { state: { activeTab: "joint-ventures-deals" } })}
                >
                    Cancel
                </button>
            </section>

            <RejectDealModal
                open={isRejectModalOpen}
                onCancel={() => {
                    setIsRejectModalOpen(false);
                    setRejectReason("");
                }}
                onReject={handleRejectDeal}
                loading={rejectJVDealLoading}
                reason={rejectReason}
                onReasonChange={setRejectReason}
            />
        </div>
    );
};

export default ProceedDetailsView;
